import { localizeDateNames } from "@/lib/dateNames";
import i18n from "@/i18n";
// Schedule drag-fill: pure planning helpers (no HTTP). Copies one source Shift
// along the same employee row; each target cell maps to one existing route.

const DAY_MS = 86400000;

export function addDaysYmd(ymd, n) {
    return new Date(Date.parse(`${ymd}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);
}

export function dayDiff(fromYmd, toYmd) {
    return Math.round((Date.parse(`${toYmd}T00:00:00Z`) - Date.parse(`${fromYmd}T00:00:00Z`)) / DAY_MS);
}

// Dates strictly after/before the source up to the target, in grid order.
export function fillRange(dates, sourceDate, targetDate) {
    const a = dates.indexOf(sourceDate);
    const b = dates.indexOf(targetDate);
    if (a < 0 || b < 0 || a === b) return [];
    return a < b ? dates.slice(a + 1, b + 1) : dates.slice(b, a);
}

// kind: create (empty) | update (1 Shift, atomic PATCH). Day codes, multi-entry,
// N/A and saving cells are never replaced — reported back as skipped.
export function planFill(row, sourceDate, targetDates, isPending) {
    const cells = new Map((row?.cells || []).map((c) => [c.date, c]));
    const src = cells.get(sourceDate);
    const shift = src && src.shifts.length === 1 && src.day_entries.length === 0 ? src.shifts[0] : null;
    if (!shift) return null;
    const offset = dayDiff(sourceDate, shift.end_date);
    const jobs = [];
    const skipped = [];
    for (const date of targetDates) {
        const cell = cells.get(date);
        const parsed = { start_time: shift.start_time, end_time: shift.end_time,
            end_date: addDaysYmd(date, offset) };
        const s = cell?.shifts || [];
        const d = cell?.day_entries || [];
        if (!cell || !cell.applicable) skipped.push({ date, reason: "not applicable" });
        else if (isPending && isPending(date)) skipped.push({ date, reason: "still saving" });
        else if (d.length) skipped.push({ date, reason: "has a day code" });
        else if (s.length > 1) skipped.push({ date, reason: "has several shifts" });
        else if (s.length === 0) jobs.push({ kind: "create", date, parsed });
        else if (s[0].start_time !== parsed.start_time || s[0].end_time !== parsed.end_time
            || s[0].end_date !== parsed.end_date) jobs.push({ kind: "update", date, parsed, shift: s[0] });
    }
    const occupied = jobs.filter((j) => j.kind === "update").length;
    return { shift, jobs, skipped, occupied };
}

const _REASONS = {
    get WORKFORCE_SHIFT_OVERLAP() { return i18n.t("wf.shiftFill._REASONS.WORKFORCE_SHIFT_OVERLAP", "overlaps another shift"); },
    get WORKFORCE_REVISION_CONFLICT() { return i18n.t("wf.shiftFill._REASONS.WORKFORCE_REVISION_CONFLICT", "changed elsewhere"); },
    get WORKFORCE_PERIOD_CLOSED() { return i18n.t("wf.shiftFill._REASONS.WORKFORCE_PERIOD_CLOSED", "period closed"); },
    get WORKFORCE_SCHEDULE_NOT_APPLICABLE() { return i18n.t("wf.shiftFill._REASONS.WORKFORCE_SCHEDULE_NOT_APPLICABLE", "not applicable"); },
};
export const fillFailureReason = (code) => _REASONS[code] || i18n.t("wf.shiftFill.notSaved", "not saved");

const _SKIP = {
    "not applicable": () => i18n.t("wf.shiftFill.skip.notApplicable", "not applicable"),
    "still saving": () => i18n.t("wf.shiftFill.skip.stillSaving", "still saving"),
    "has a day code": () => i18n.t("wf.shiftFill.skip.hasADayCode", "has a day code"),
    "has several shifts": () => i18n.t("wf.shiftFill.skip.hasSeveralShifts", "has several shifts"),
};
const _WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
// "Wed 16.9. (has a day code), Thu 17.9. (overlaps another shift)"
export function describeNotCopied(items) {
    return items.map(({ date, reason }) => {
        const dt = new Date(`${date}T00:00:00Z`);
        return `${localizeDateNames(_WD[dt.getUTCDay()])} ${dt.getUTCDate()}.${dt.getUTCMonth() + 1}. (${_SKIP[reason] ? _SKIP[reason]() : reason})`;
    }).join(", ");
}
